import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSectionFields, useSectionsByType } from "@/queries/use-custom-fields.query.ts";
import { ApiCFEntityTypes } from "@/types/custom-field.types.ts";
import {
  buildJobApplyFormLayout,
  JobApplyFormLayoutField,
  JobApplyFormLayoutSection,
  moveJobApplyFormField,
  moveJobApplyFormSection,
  toJobApplyFormSaveFromLayout,
} from "@/pages/recruit/jobs/edit/job-apply-form.utils.ts";
import { ApiApplyFormSectionSave } from "@/types/recruit/job.types.ts";
import {
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface JobEditCareerSiteFieldsModalProps extends ModalProps {
  config: ApiApplyFormSectionSave[] | null;
  publicTitle: string | null;
  onChange: (config: ApiApplyFormSectionSave[]) => void;
  onPublicTitleChange?: (publicTitle: string) => void;
}

export function JobEditCareerSiteFieldsModal({
  config,
  publicTitle,
  onChange,
  onPublicTitleChange,
  ...props
}: JobEditCareerSiteFieldsModalProps) {
  const { t } = useTranslation();
  const { getFieldTitle, getSectionTitle } = useCustomFieldsHook();
  const { close } = useModals();
  const { id } = props;
  const { data: sections, isLoading: isSectionsIsLoading } = useSectionsByType(
    ApiCFEntityTypes.applicant
  );
  const { data: fields, isLoading: isFieldsIsLoading } = useSectionFields(
    ApiCFEntityTypes.applicant
  );
  const [localPublicTitle, setLocalPublicTitle] = useState(publicTitle ?? "");
  const [layoutConfig, setLayoutConfig] = useState(config);
  const layout: JobApplyFormLayoutSection[] = useMemo(
    () => buildJobApplyFormLayout(sections ?? [], fields ?? [], layoutConfig),
    [sections, fields, layoutConfig]
  );

  const applyLayout = (nextLayout: JobApplyFormLayoutSection[]) => {
    const nextConfig = toJobApplyFormSaveFromLayout(nextLayout);
    setLayoutConfig(nextConfig);
    onChange(nextConfig);
  };

  const updateField = (fieldId: number, update: Partial<JobApplyFormLayoutField>) =>
    applyLayout(
      layout.map((section) => ({
        ...section,
        fields: section.fields.map((item) =>
          item.field.id === fieldId ? { ...item, ...update } : item
        ),
      }))
    );

  const handleDragEnd = ({ type, source, destination }: DropResult) => {
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }
    if (type === "section") {
      applyLayout(moveJobApplyFormSection(layout, source.index, destination.index));
      return;
    }
    const getSectionId = (droppableId: string) => Number(droppableId.replace("fields-", ""));
    applyLayout(
      moveJobApplyFormField(
        layout,
        { sectionId: getSectionId(source.droppableId), index: source.index },
        { sectionId: getSectionId(destination.droppableId), index: destination.index }
      )
    );
  };

  return (
    <Modal
      title={t("ats:setup_job_page")}
      {...props}
      withCloser={true}
      actions={
        <Stack>
          <Button
            size={"lg"}
            onClick={() => {
              onPublicTitleChange?.(localPublicTitle);
              close(id);
            }}
          >
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <Stack>
        {onPublicTitleChange && (
          <>
            <Input
              value={localPublicTitle}
              onChange={(e) => setLocalPublicTitle(e.target.value)}
              label={t("ats:public_title")}
            />
            <Stack className={"pt-2 text-secondary"}>{t("ats:public_title_information")}</Stack>
          </>
        )}
        <Title
          header={t("ats:setup_job_fields_title")}
          caption={t("ats:setup_job_fields_description")}
          paddingBottom
          paddingTop
        />
        {isFieldsIsLoading || isSectionsIsLoading ? (
          <Stack gap={"md"}>
            <Skeleton className={"w-full h-10 rounded-sm"} />
            <Skeleton className={"w-full h-10 rounded-sm"} />
            <Skeleton className={"w-full h-10 rounded-sm"} />
            <Skeleton className={"w-full h-10 rounded-sm"} />
          </Stack>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId={"sections"} type={"section"}>
              {(sectionsProvided) => (
                <div ref={sectionsProvided.innerRef} {...sectionsProvided.droppableProps}>
                  {layout.map(({ section, fields: sectionFields }, sectionIndex) => (
                    <Draggable
                      key={`section-${section.id}`}
                      draggableId={`section-${section.id}`}
                      index={sectionIndex}
                    >
                      {(sectionProvided) => (
                        <div ref={sectionProvided.innerRef} {...sectionProvided.draggableProps}>
                          <Stack
                            direction={"horizontal"}
                            items={"center"}
                            gap={"xs"}
                            className={"cursor-move pb-2 pt-4 pl-2"}
                            {...sectionProvided.dragHandleProps}
                          >
                            {/*<FontAwesomeIcon*/}
                            {/*  className={"text-secondary"}*/}
                            {/*  icon={"fa-light fa-grip-dots-vertical"}*/}
                            {/*/>*/}
                            <Title
                              header={getSectionTitle(section)}
                              size={"xs"}
                              variant={"secondary"}
                            />
                          </Stack>
                          <Droppable droppableId={`fields-${section.id}`} type={"field"}>
                            {(fieldsProvided) => (
                              <div
                                ref={fieldsProvided.innerRef}
                                {...fieldsProvided.droppableProps}
                                className={"min-h-10"}
                              >
                                {sectionFields.map(
                                  (
                                    { field, is_mandatory: isJobMandatory, is_hidden: isHidden },
                                    fieldIndex
                                  ) => {
                                    const isMandatory = field.is_mandatory || isJobMandatory;
                                    const canHide = !isMandatory;
                                    return (
                                      <Draggable
                                        key={`field-${field.id}`}
                                        draggableId={`field-${field.id}`}
                                        index={fieldIndex}
                                      >
                                        {(fieldProvided) => (
                                          <div
                                            ref={fieldProvided.innerRef}
                                            {...fieldProvided.draggableProps}
                                            {...fieldProvided.dragHandleProps}
                                            className={"pb-2"}
                                          >
                                            <Stack
                                              className={`py-1 pl-2 pr-1 border border-dark/5 bg-dark/5 rounded-xs group ${isHidden ? "text-secondary" : ""}`}
                                              direction={"horizontal"}
                                              justify={"between"}
                                              items={"center"}
                                              gap={"sm"}
                                            >
                                              <Stack
                                                direction={"horizontal"}
                                                items={"center"}
                                                gap={"xs"}
                                              >
                                                <FontAwesomeIcon
                                                  className={"text-secondary"}
                                                  icon={"fa-light fa-grip-dots-vertical"}
                                                />
                                                {getFieldTitle(field)}
                                              </Stack>

                                              <Stack direction={"horizontal"}>
                                                {canHide && (
                                                  <Tooltip delay={true}>
                                                    <TooltipTrigger>
                                                      <Box
                                                        className={
                                                          isHidden
                                                            ? "p-1 cursor-pointer"
                                                            : "p-1 cursor-pointer opacity-0 group-hover:opacity-100 transition"
                                                        }
                                                        onClick={() =>
                                                          updateField(field.id, {
                                                            is_hidden: !isHidden,
                                                          })
                                                        }
                                                      >
                                                        <FontAwesomeIcon
                                                          icon={
                                                            isHidden
                                                              ? "fa-light fa-eye-slash"
                                                              : "fa-light fa-eye"
                                                          }
                                                        />
                                                      </Box>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      {isHidden
                                                        ? t("common:show")
                                                        : t("common:hide")}
                                                    </TooltipContent>
                                                  </Tooltip>
                                                )}
                                                {field.is_mandatory && (
                                                  <Box className={"p-1"}>
                                                    <FontAwesomeIcon
                                                      className={"text-danger"}
                                                      icon={"fa-light fa-asterisk"}
                                                    />
                                                  </Box>
                                                )}
                                                {!field.is_mandatory && !isHidden && (
                                                  <Tooltip delay={true}>
                                                    <TooltipTrigger>
                                                      <Box
                                                        className={
                                                          isMandatory
                                                            ? "p-1 cursor-pointer"
                                                            : "p-1 cursor-pointer opacity-0 group-hover:opacity-100 transition"
                                                        }
                                                        onClick={() =>
                                                          updateField(field.id, {
                                                            is_mandatory: !isMandatory,
                                                            is_hidden: false,
                                                          })
                                                        }
                                                      >
                                                        <FontAwesomeIcon
                                                          className={
                                                            isMandatory ? "text-danger" : ""
                                                          }
                                                          icon={"fa-light fa-asterisk"}
                                                        />
                                                      </Box>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                      {isMandatory
                                                        ? t("ats:make_optional")
                                                        : t("ats:make_mandatory")}
                                                    </TooltipContent>
                                                  </Tooltip>
                                                )}
                                              </Stack>
                                            </Stack>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  }
                                )}
                                {fieldsProvided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {sectionsProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Stack>
    </Modal>
  );
}
